import { Fragment, type ReactNode, useMemo } from 'react';
import { Box, Divider, Grid, Typography, type TypographyProps } from '@lingoda/ui';
import { trans } from '@lingoda/i18n';
import { Location } from '@lingoda/monolith-graphql';
import { styled } from '@lingoda/ui/theme';
import { CheckoutBillingCancellationButton } from '@lingoda/learning-teams';
import {
    ContentBlockTypography,
    findAllContentBlocks,
    findContentBlock,
} from '@lingoda/content-blocks';
import { PaperStyled } from '../PaperStyled';
import SectionWrapper from '../CheckoutForm/SectionWrapper';
import { type CheckoutPurchase } from '../types';
import { CouponLayout } from './CouponLayout';

interface Props {
    purchase: CheckoutPurchase;
    couponSection: ReactNode;
    currencySection: ReactNode;
}

const SummaryRow = ({ children }: { readonly children: ReactNode }) => (
    <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            {children}
        </Box>
    </Grid>
);

interface SummaryLabelProps {
    variant?: TypographyProps['variant'];
    fontWeight?: TypographyProps['fontWeight'];
    children: ReactNode;
}

const SummaryLabel = ({
    children,
    variant = 'body1',
    fontWeight = 'normal',
}: SummaryLabelProps) => (
    <Typography variant={variant} fontWeight={fontWeight} htmlColor="text.secondary">
        {children}
    </Typography>
);

const SummaryContent = styled(Grid)(({ theme }) => ({
    rowGap: theme.spacing(2),
}));

const Summary = ({ couponSection, currencySection, purchase }: Props) => {
    const { contentBlocks } = purchase;
    const content = useMemo(
        () => ({
            title: findContentBlock(contentBlocks, Location.SummaryTitle),
            summary: findAllContentBlocks(contentBlocks, Location.SummaryContent) || [],
            priceLines:
                findAllContentBlocks(contentBlocks, [
                    Location.SummaryDiscount,
                    Location.SummaryDiscountNote,
                    Location.SummaryPrice,
                ]) || [],
            billingAndCancellation: findContentBlock(
                contentBlocks,
                Location.BillingAndCancellation,
            ),
        }),
        [contentBlocks],
    );

    return (
        <PaperStyled variant="outlined">
            <SectionWrapper>
                <Grid container alignContent="center" justify="center" spacing={2}>
                    <SummaryRow>
                        <Typography variant="h2">
                            {trans('purchase-summary', {}, 'public-payment')}
                        </Typography>
                        <Box minWidth={125}>{currencySection}</Box>
                    </SummaryRow>
                    {content.title && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle1">{content.title.text}</Typography>
                        </Grid>
                    )}
                    {content.summary.length > 0 && (
                        <SummaryContent item container xs={12}>
                            {content.summary.map(({ label, text }, index) => (
                                <SummaryRow key={`${index}-${label}`}>
                                    <SummaryLabel>{label}</SummaryLabel>
                                    <Typography
                                        variant="body1"
                                        textAlign="right"
                                        sx={{
                                            whiteSpace: 'pre-line',
                                        }}
                                    >
                                        {text}
                                    </Typography>
                                </SummaryRow>
                            ))}
                        </SummaryContent>
                    )}
                    <Grid item xs={12}>
                        <Box my={4}>
                            <CouponLayout isCouponAvailable={purchase.canUseCoupon}>
                                {couponSection}
                            </CouponLayout>
                        </Box>
                    </Grid>
                    {content.priceLines.map((priceLine, index) => {
                        const isFinalPrice = index === content.priceLines.length - 1;

                        return (
                            <Fragment key={index}>
                                {isFinalPrice && content.priceLines.length > 1 && (
                                    <Box width="100%" paddingBottom={1}>
                                        <Divider />
                                    </Box>
                                )}
                                <SummaryRow>
                                    <SummaryLabel
                                        variant={
                                            priceLine.location === Location.SummaryDiscountNote
                                                ? 'body2'
                                                : 'body1'
                                        }
                                        fontWeight={
                                            priceLine.location === Location.SummaryDiscountNote
                                                ? '600'
                                                : 'normal'
                                        }
                                    >
                                        {priceLine.label}
                                    </SummaryLabel>
                                    <ContentBlockTypography
                                        {...priceLine}
                                        data-cy={isFinalPrice ? 'purchase-subtotal' : undefined}
                                        variant={isFinalPrice ? 'h3' : 'body1'}
                                        sx={{ alignSelf: 'flex-start' }}
                                    />
                                </SummaryRow>
                            </Fragment>
                        );
                    })}
                    {!!content.billingAndCancellation && (
                        <Grid item xs={12}>
                            <CheckoutBillingCancellationButton
                                credits={Number(content.billingAndCancellation.text)}
                            />
                        </Grid>
                    )}
                </Grid>
            </SectionWrapper>
        </PaperStyled>
    );
};

export default Summary;
